import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import ReportGenerator from './ReportGenerator';

// Un solo tono para el dinero: todas las gráficas hablan de lo mismo, así que no
// hace falta color categórico. Los colores de estado se reservan para la tasa.
const AZUL = '#2a78d6';
const ESTADO = { bueno: '#0ca30c', aviso: '#fab219', critico: '#d03b3b' };
const TINTA = '#374151';
const TINTA_SUAVE = '#6b7280';

const pesos = (n) => '$' + Math.round(n || 0).toLocaleString('es-CO');

// '2026-07' -> 'jul 2026'
const mesLegible = (clave) => {
  if (!clave) return '';
  const [anio, mes] = String(clave).split('-');
  const nombres = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  return `${nombres[parseInt(mes, 10) - 1] || ''} ${anio}`;
};

function Medidor({ porcentaje, conPago, total }) {
  const color = porcentaje >= 75 ? ESTADO.bueno : porcentaje >= 50 ? ESTADO.aviso : ESTADO.critico;
  const etiqueta = porcentaje >= 75 ? 'Buena' : porcentaje >= 50 ? 'Regular' : 'Baja';

  return (
    <div style={{ background: 'white', borderRadius: '10px', padding: '20px', border: '1px solid #e5e7eb' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
        <span style={{ color: TINTA_SUAVE, fontSize: '14px' }}>Tasa de cobro</span>
        {/* El color de estado nunca va solo: siempre lleva su palabra al lado. */}
        <span style={{ color, fontWeight: 'bold', fontSize: '14px' }}>{etiqueta}</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '40px', fontWeight: 'bold', color: TINTA, lineHeight: 1 }}>
          {Math.round(porcentaje)}%
        </span>
        <span style={{ color: TINTA_SUAVE, fontSize: '14px' }}>
          {conPago} de {total} préstamos con algún pago
        </span>
      </div>

      <div style={{ height: '10px', background: '#f3f4f6', borderRadius: '5px', overflow: 'hidden' }}>
        <div style={{
          width: `${Math.min(100, Math.max(0, porcentaje))}%`,
          height: '100%',
          background: color,
          borderRadius: '5px',
          transition: 'width .4s'
        }} />
      </div>
    </div>
  );
}

function Vacio({ mensaje }) {
  return (
    <div style={{
      padding: '40px 20px', textAlign: 'center', color: TINTA_SUAVE,
      background: '#f9fafb', borderRadius: '10px', border: '1px dashed #d1d5db'
    }}>
      {mensaje}
    </div>
  );
}

export default function CobranzaAnalysis() {
  const [monthlyData, setMonthlyData] = useState([]);
  const [collectionRate, setCollectionRate] = useState({});
  const [topPayers, setTopPayers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [monthly, rate, payers] = await Promise.all([
          axios.get('/api/cobranza/monthly'),
          axios.get('/api/cobranza/rate'),
          axios.get('/api/cobranza/top-payers')
        ]);
        // El servidor los manda del más reciente al más antiguo; un eje de
        // tiempo tiene que ir al revés.
        setMonthlyData([...(monthly.data || [])].reverse());
        setCollectionRate(rate.data || {});
        setTopPayers(payers.data || []);
      } catch (err) {
        console.error('Error cargando cobranza:', err);
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <p>Cargando cobranza...</p>;

  const serie = monthlyData.map(m => ({
    mes: mesLegible(m.month),
    cobrado: m.total_collected || 0,
    pagos: m.payment_count || 0
  }));

  const totalCobrado = serie.reduce((s, m) => s + m.cobrado, 0);
  const totalPagos = serie.reduce((s, m) => s + m.pagos, 0);
  const mejorMes = serie.reduce((a, b) => (b.cobrado > (a ? a.cobrado : 0) ? b : a), null);
  const promedioMes = serie.length ? totalCobrado / serie.length : 0;
  const maxPagado = Math.max(...topPayers.map(p => p.total_paid || 0), 1);

  return (
    <div className="cobranza-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <h2 style={{ margin: 0 }}>📈 Cobranza</h2>
        <ReportGenerator data={topPayers} type="cobranza" fileName="cobranza" />
      </div>

      {/* La cifra que se busca al entrar: cuánto dinero ha entrado */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '12px', padding: '28px', color: 'white', marginBottom: '20px'
      }}>
        <div style={{ opacity: 0.85, fontSize: '14px', marginBottom: '6px' }}>
          Cobrado en los últimos {serie.length || 12} meses
        </div>
        <div style={{ fontSize: '48px', fontWeight: 'bold', lineHeight: 1.1 }}>
          {pesos(totalCobrado)}
        </div>
        <div style={{ opacity: 0.85, fontSize: '14px', marginTop: '8px' }}>
          {totalPagos} pagos · promedio {pesos(promedioMes)} al mes
          {mejorMes && ` · mejor mes: ${mejorMes.mes} (${pesos(mejorMes.cobrado)})`}
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <Medidor
          porcentaje={collectionRate.collection_rate || 0}
          conPago={collectionRate.loans_with_payment || 0}
          total={collectionRate.total_loans || 0}
        />
      </div>

      {/* Tendencia con una sola serie y un solo eje. Antes convivían el monto
          (cientos de miles) y el número de pagos (unidades) en el mismo eje, y
          las barras de pagos quedaban aplastadas contra el suelo. */}
      <div style={{ background: 'white', borderRadius: '10px', padding: '20px', border: '1px solid #e5e7eb', marginBottom: '20px' }}>
        <h3 style={{ marginTop: 0, marginBottom: '4px', fontSize: '16px', color: TINTA }}>
          Dinero cobrado por mes
        </h3>
        <p style={{ marginTop: 0, color: TINTA_SUAVE, fontSize: '13px' }}>
          El número de pagos aparece al pasar el ratón.
        </p>

        {serie.length === 0 ? (
          <Vacio mensaje="Todavía no hay pagos registrados." />
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={serie} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="degradadoCobro" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={AZUL} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={AZUL} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="mes"
                tick={{ fill: TINTA_SUAVE, fontSize: 12 }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: TINTA_SUAVE, fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                width={70}
                tickFormatter={(v) => (v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : `$${Math.round(v / 1000)}K`)}
              />
              <Tooltip
                // Defensivo a propósito: si Recharts no entrega el payload, el
                // tooltip muestra el importe igualmente en vez de romperse.
                formatter={(valor, nombre, item) => [
                  item && item.payload
                    ? `${pesos(valor)} · ${item.payload.pagos} pagos`
                    : pesos(valor),
                  'Cobrado'
                ]}
                labelFormatter={(etiqueta) => etiqueta}
                contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
              />
              <Area
                type="monotone"
                dataKey="cobrado"
                stroke={AZUL}
                strokeWidth={2}
                fill="url(#degradadoCobro)"
                dot={{ r: 3, fill: AZUL, strokeWidth: 0 }}
                activeDot={{ r: 6, fill: AZUL, stroke: 'white', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Ranking en barras: comparar magnitudes se lee mejor que una columna de
          cifras, y cada barra lleva su valor escrito al lado. */}
      <div style={{ background: 'white', borderRadius: '10px', padding: '20px', border: '1px solid #e5e7eb' }}>
        <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '16px', color: TINTA }}>
          Quién más ha pagado
        </h3>

        {topPayers.length === 0 ? (
          <Vacio mensaje="Ningún cliente ha registrado pagos todavía." />
        ) : (
          <div>
            {topPayers.map((p, i) => (
              <div key={p.cedula || i} style={{ marginBottom: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px', gap: '10px' }}>
                  <span style={{ color: TINTA, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <strong>{i + 1}.</strong> {p.name}
                    {p.cedula && <span style={{ color: TINTA_SUAVE, fontSize: '12px' }}> · {p.cedula}</span>}
                  </span>
                  <span style={{ color: TINTA, fontWeight: 'bold', fontSize: '14px', whiteSpace: 'nowrap' }}>
                    {pesos(p.total_paid)}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ flex: 1, height: '8px', background: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${((p.total_paid || 0) / maxPagado) * 100}%`,
                      height: '100%',
                      background: AZUL,
                      borderRadius: '4px'
                    }} />
                  </div>
                  <span style={{ color: TINTA_SUAVE, fontSize: '12px', whiteSpace: 'nowrap' }}>
                    {p.payment_count} pagos
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {totalPagos === 0 && (
        <div style={{
          marginTop: '20px', padding: '15px', borderRadius: '8px',
          background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: '14px'
        }}>
          ⚠️ No aparece ningún pago. Si sabes que sí has cobrado, es posible que los
          pagos estén registrados contra préstamos que ya no existen.
        </div>
      )}
    </div>
  );
}
